import { useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SendRounded } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { assistantApi, type AssistantAnswer } from '@/api/setupGuide.api';
import { APP_NAME } from '@/config/productBrand';
import { humanizeError } from '@/utils/errorMessage';

interface Turn {
  question: string;
  answer?: AssistantAnswer;
  error?: string;
}

const SUGGESTIONS = [
  'How do I add a team member?',
  'How do I connect my email?',
  'What is left in my setup?',
];

/**
 * The in-product assistant (Module 0, O11): ask "how do I…", get a short answer, "Take me there" opens the
 * screen it names and "Show me step by step" lists the clicks. POST /api/assistant/ask.
 */
export default function AssistantPanel({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [turns, stepsOpen]);

  const ask = async (text?: string) => {
    const question = (text ?? input).trim();
    if (!question || busy) return;
    setInput('');
    setStepsOpen(false);
    setBusy(true);
    setTurns((t) => [...t, { question }]);
    try {
      const answer = await assistantApi.ask(question, location.pathname);
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, answer } : x)));
    } catch (e) {
      const error = humanizeError(e, 'The assistant couldn’t answer just now. Please try again.');
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, error } : x)));
    } finally {
      setBusy(false);
    }
  };

  const last = turns[turns.length - 1];

  return (
    <Box>
      <Stack spacing={1.25} sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.25 }}>
        {turns.length === 0 && (
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
            {SUGGESTIONS.map((s) => (
              <Chip key={s} label={s} size="small" variant="outlined" onClick={() => { void ask(s); }} sx={{ cursor: 'pointer' }} />
            ))}
          </Stack>
        )}

        {turns.map((t, i) => (
          <Box key={i}>
            <Box sx={(th) => ({ bgcolor: alpha(th.palette.primary.main, 0.08), borderRadius: 2, px: 1.75, py: 1.25 })}>
              <Typography variant="body2" color="primary.main" sx={{ overflowWrap: 'anywhere' }}>{t.question}</Typography>
            </Box>
            {!t.answer && !t.error && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, px: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Thinking…</Typography>
              </Stack>
            )}
            {t.error && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1, px: 0.5 }}>{t.error}</Typography>
            )}
            {t.answer && (
              <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 2, px: 1.75, py: 1.25 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.65, overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>
                  {t.answer.answer}
                </Typography>
                {i === turns.length - 1 && stepsOpen && t.answer.steps.length > 0 && (
                  <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                    {t.answer.steps.map((s, n) => (
                      <Typography key={n} component="li" variant="body2" sx={{ lineHeight: 1.6 }}>{s}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ))}
        <div ref={endRef} />
      </Stack>

      {last?.answer && (last.answer.link || last.answer.steps.length > 0) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {last.answer.link && (
            <Button
              variant="contained"
              size="small"
              onClick={() => { navigate(last.answer!.link!); onNavigate?.(); }}
            >
              Take me there
            </Button>
          )}
          {last.answer.steps.length > 0 && (
            <Button variant="outlined" size="small" onClick={() => setStepsOpen((o) => !o)}>
              {stepsOpen ? 'Hide steps' : 'Show me step by step'}
            </Button>
          )}
        </Stack>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder={`Ask anything about ${APP_NAME}…`}
        value={input}
        onChange={(e) => setInput(e.target.value.slice(0, 500))}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(); } }}
        disabled={busy}
        sx={{ mt: 1.5 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Ask" onClick={() => { void ask(); }} disabled={busy || !input.trim()}>
                  <SendRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, lineHeight: 1.5 }}>
        Available on every screen (bottom-right). Knows the whole product and your setup progress.
      </Typography>
    </Box>
  );
}

/** Opens the bottom-right assistant from anywhere (e.g. the Help menu). */
export const openAssistant = () => window.dispatchEvent(new Event('travelerp:open-assistant'));
